/**
 * Controllers (hooks) for the app shell: sidebar state, command palette and logout.
 * The views in Sidebar.js / CommandPalette.js / TopBar.js only render what these return.
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { User } from '../../../data';
import { filterMenuByAccess } from '../../../core/access';
import { getActiveCompany, getCompanyIdSync, isScreenEnabled } from '../../../company/session';
import { refreshFeatureFlags } from '../../../data/central';
import { reloadTo, resetClientState } from '../../../company/reset';
import { ClientList, companySidebarList, extraNavKeys, sidebarKeys, sidebarList } from './list';
import { ROLE_LABELS, getSearchItems, navTrail } from './navigation.model';

const COLLAPSED_KEY = 'shelf.sidebarCollapsed';

const readCollapsed = () => {
    try {
        return localStorage.getItem(COLLAPSED_KEY) === '1';
    } catch {
        return false;
    }
};

/**
 * Signs the user out: forgets the session, its company and everything cached for them
 * (company/reset.js), then loads the landing page fresh so no state survives.
 */
export function useLogout() {
    return React.useCallback(() => {
        resetClientState();
        reloadTo('/');
    }, []);
}

/** Current user identity for the shell. */
export function useShellUser() {
    const user = User.getInstance();
    return {
        user,
        userType: user?.userType,
        clientId: user?.client,
        name: user?.name || 'Usuario',
        roleLabel: ROLE_LABELS[user?.userType] || 'Asesor',
        // The company this session works in, shown under the Shelf brand.
        workspaceLabel: getActiveCompany().workspaceLabel,
        initials: (user?.name || 'U')
            .split(/\s+/)
            .filter(Boolean)
            .slice(0, 2)
            .map((p) => p[0]?.toUpperCase())
            .join(''),
        // True while the auth check hasn't populated the user singleton yet.
        loading: !user
    };
}

/** Sidebar state + actions. */
export function useSidebarController(current) {
    const navigate = useNavigate();
    const logout = useLogout();
    const shellUser = useShellUser();
    const { userType, clientId } = shellUser;

    const [collapsed, setCollapsedState] = React.useState(readCollapsed);
    const [mobileOpen, setMobileOpen] = React.useState(false);
    const [searchOpen, setSearchOpen] = React.useState(false);
    const [, setFlagsVersion] = React.useState(0);

    // Feature flags from central-service (throttled): a change redraws the menu.
    React.useEffect(() => {
        let alive = true;
        const companyId = getCompanyIdSync();
        if (companyId) {
            refreshFeatureFlags(companyId).then((changed) => {
                if (alive && changed) setFlagsVersion((v) => v + 1);
            });
        }
        return () => {
            alive = false;
        };
    }, []);

    const setCollapsed = React.useCallback((value) => {
        setCollapsedState(value);
        try {
            localStorage.setItem(COLLAPSED_KEY, value ? '1' : '0');
        } catch {
            // Per-viewer convenience only.
        }
    }, []);

    // Clients keep their bespoke menu ("Mi Empresa" etc.); every other role is the master list
    // filtered by the access model.
    // Both are limited to the screens the signed-in company has.
    const menuItems = userType === 'client' ? ClientList.filter((item) => isScreenEnabled(item.key)) : filterMenuByAccess(companySidebarList, userType);

    // Open the submenu that contains the current page.
    const defaultOpenKeys = React.useMemo(() => {
        const parent = sidebarList.find((item) => item.children?.some((c) => c.key === current));
        return parent ? [parent.key] : [];
    }, [current]);

    // ⌘K / Ctrl+K opens the command palette.
    React.useEffect(() => {
        const onKeyDown = (e) => {
            if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
                e.preventDefault();
                setSearchOpen(true);
            }
        };
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, []);

    const onMenuClick = (e) => {
        if (e.key === 'my-client' && clientId) {
            navigate(`/dashboard/clients/client?id=${clientId}`);
            setMobileOpen(false);
            return;
        }
        const item = sidebarKeys.find((i) => i.key === e.key) || extraNavKeys.find((i) => i.key === e.key);
        if (item?.href) {
            navigate(item.href);
            setMobileOpen(false);
        }
    };

    return {
        ...shellUser,
        current,
        trail: navTrail(current, companySidebarList) || [],
        menuItems,
        defaultOpenKeys,
        collapsed,
        setCollapsed,
        toggleCollapsed: () => setCollapsed(!collapsed),
        mobileOpen,
        openMobile: () => setMobileOpen(true),
        closeMobile: () => setMobileOpen(false),
        searchOpen,
        openSearch: () => setSearchOpen(true),
        closeSearch: () => setSearchOpen(false),
        navigate,
        onMenuClick,
        logout
    };
}

/** Command palette state: filtering + keyboard navigation over the user's reachable pages. */
export function useCommandPalette({ open, onClose, navigate, userType, clientId }) {
    const [searchTerm, setSearchTerm] = React.useState('');
    const [selectedIndex, setSelectedIndex] = React.useState(0);

    const searchItems = React.useMemo(() => getSearchItems(userType, clientId), [userType, clientId]);

    const items = React.useMemo(() => {
        if (!searchTerm) return searchItems;
        const term = searchTerm.toLowerCase();
        return searchItems.filter(
            (item) => item.label.toLowerCase().includes(term) || item.description.toLowerCase().includes(term)
        );
    }, [searchTerm, searchItems]);

    React.useEffect(() => setSelectedIndex(0), [items.length]);

    const close = () => {
        setSearchTerm('');
        setSelectedIndex(0);
        onClose();
    };

    const select = (item) => {
        navigate(item.href);
        close();
    };

    const onKeyDown = (e) => {
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setSelectedIndex((prev) => Math.min(prev + 1, items.length - 1));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setSelectedIndex((prev) => Math.max(prev - 1, 0));
        } else if (e.key === 'Enter' && items[selectedIndex]) {
            e.preventDefault();
            select(items[selectedIndex]);
        }
    };

    return { open, searchTerm, setSearchTerm, selectedIndex, setSelectedIndex, items, select, close, onKeyDown };
}
